// packages/components/src/support-ai/AIAgentPanel.tsx
import React, { useState } from 'react';
import { Card, Button } from '@dopomogai/ui';
import { Lightbulb, User, FileArchive, Zap, Activity, ShieldQuestion, Edit } from 'lucide-react';
import { ActionPlanPanel } from './ActionPlanPanel';
import { TicketSummary } from './TicketSummary';
import { DocumentsTab } from '../doctor-ai/DocumentsTab';
import type { UIMessageOption, SolutionOption, ExecutionPlan, AIDiagnostics } from '@dopomogai/types';

// --- Generic shapes for props ---
interface ProfileData {
  id: string;
  name: string;
  company: string;
  avatarUrl?: string;
  profile: Record<string, any>;
  files: any[];
}
interface TicketSummaryData {
  initialQuery: string;
  translatedInitialQuery?: string;
  status: 'Open' | 'Pending' | 'Resolved' | 'Escalated';
  interactionCount: number;
}
interface AIAgentPanelProps<T extends ProfileData> {
  customer: T;
  ticketSummary: TicketSummaryData;
  diagnostics?: AIDiagnostics;
  messageOptions?: UIMessageOption[];
  solutionOptions?: SolutionOption[];
  executionPlan?: ExecutionPlan;
  onFileAdd: (files: File[]) => void;
  onFileRemove: (fileId: string) => void;
  onMessageSelect: (message: UIMessageOption) => void;
  onSolutionSelect: (option: SolutionOption) => void;
  onPlanApply: () => void;
}

const PanelSection: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }> = ({ title, icon, children, action }) => (
  <div className="p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="flex items-center gap-2 text-md font-bold text-slate-800">{icon} {title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export const AIAgentPanel = <T extends ProfileData>({
  customer,
  ticketSummary,
  diagnostics,
  messageOptions,
  solutionOptions,
  executionPlan,
  onFileAdd,
  onFileRemove,
  onMessageSelect,
  onSolutionSelect,
  onPlanApply,
}: AIAgentPanelProps<T>) => {
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());

  // Adapter to transform customer.files into the shape DocumentsTab expects
  const documentsForTab = customer.files.map(doc => ({
    id: doc.id,
    status: doc.status || 'processed',
    file: {
      name: doc.name,
      size: doc.size || 0,
    }
  }));

  const handleFileSelectionChange = (fileId: string, isSelected: boolean) => {
    setSelectedFileIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(fileId);
      } else {
        newSet.delete(fileId);
      }
      return newSet;
    });
  };

  const handleSyncToRag = () => {
    if (selectedFileIds.size === 0) {
      alert("Please select one or more files to update the AI's knowledge source.");
      return;
    }
    alert(`Syncing ${selectedFileIds.size} file(s) to the AI knowledge base for this session. (Demo)`);
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <header className="flex items-center gap-4 p-4 border-b-2 border-black bg-white flex-shrink-0">
        <img src={customer.avatarUrl || '/avatars/default-avatar.png'} alt={customer.name} className="h-14 w-14 rounded-full border-2 border-black object-cover"/>
        <div>
          <h2 className="text-xl font-bold text-slate-900">AI Agent</h2>
          <p className="text-sm text-slate-600">Assisting with: {customer.name}</p>
        </div>
      </header>
      
      <div className="flex-grow overflow-y-auto p-5 space-y-5">
        <TicketSummary {...ticketSummary} />

        {diagnostics?.insight && (
          <Card noHoverEffect className="bg-sky-50 border-sky-200">
            <div className="p-4">
              <h4 className="flex items-center gap-2 text-md font-bold text-sky-800 mb-2"><Lightbulb size={18} /> AI Insight</h4>
              <p className="text-sm text-sky-700">{diagnostics.insight}</p>
              {diagnostics.sourceDocument && (
                 <div className="text-right mt-2">
                    <a href="#" onClick={e => {e.preventDefault(); alert(`Navigating to source: ${diagnostics.sourceDocument?.name}`)}} className="text-xs font-semibold text-sky-600 hover:underline">Source: {diagnostics.sourceDocument.name}</a>
                 </div>
              )}
            </div>
          </Card>
        )}

        <Card noHoverEffect>
          <PanelSection title="Action Plan" icon={<Zap size={20} />}>
            <ActionPlanPanel {...{messageOptions, solutionOptions, executionPlan, onMessageSelect, onSolutionSelect, onPlanApply}} />
          </PanelSection>
        </Card>
        
        <details open className="border-2 border-black bg-slate-100 rounded-xl">
          <summary className="cursor-pointer font-bold text-slate-800 list-none flex items-center gap-2 p-5">
            <Activity size={18} /> Context & Details
            <span className="text-slate-500 ml-auto text-xs font-normal">(Click to expand)</span>
          </summary>
          <div className="p-5 pt-0 space-y-5 animate-in fade-in-0 slide-in-from-top-2 duration-300">
            {diagnostics?.summary && (
               <Card noHoverEffect>
                  <PanelSection title="AI Justification" icon={<ShieldQuestion size={18}/>}>
                     <p className="text-sm text-slate-600">{diagnostics.summary}</p>
                  </PanelSection>
              </Card>
            )}
            <Card noHoverEffect>
              <PanelSection title="Attached Files & RAG Control" icon={<FileArchive size={18}/>} action={<Button text="Add Note" variant="outline" size="sm" icon={<Edit size={14}/>} onClick={() => alert('Adding a note is a planned feature.')}/>}>
                  <DocumentsTab
                      documents={documentsForTab}
                      onFileAdd={onFileAdd}
                      onFileRemove={onFileRemove}
                      selectedFileIds={selectedFileIds}
                      onFileSelectionChange={handleFileSelectionChange}
                  />
                  <div className="mt-4 border-t border-slate-200 pt-4">
                      <Button 
                        text={`Update AI Knowledge (${selectedFileIds.size})`} 
                        variant="secondary" fullWidth onClick={handleSyncToRag}
                        disabled={selectedFileIds.size === 0}
                      />
                  </div>
              </PanelSection>
            </Card>
          </div>
        </details>
      </div>
    </div>
  );
};